import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { format, parse, isValid } from 'date-fns';

import Text from '../../../../shared/_TBComponents/Text';
import Toggle from '../../../../shared/_TBComponents/Toggle';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  background: ${(props) => props.theme.colors.surface};
  border: 1px solid ${(props) => props.theme.colors.borderLight};
  border-radius: 4px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const LinkButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;

  label {
    margin-bottom: 0;
  }
`;

const LockNote = styled.p`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 13px;
  color: #A35200;
`;

const formatDate = (dateSet) => {
  const parsed = dateSet ? parse(dateSet, 'yyyy-MM-dd', new Date()) : null;
  return parsed && isValid(parsed) ? format(parsed, 'd MMM yyyy') : dateSet;
};

const CardLabel = () => (
  <Text
    upperCase
    styledTextProps={{
      fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', color: '#9E9E9E',
    }}
  >
    Current max
  </Text>
);

// When locked the athlete's results only save to history
const CurrentMaxCard = ({
  value, dateSet, sourceLabel, athleteFirst, isLocked, isUpdatingLock, isClearing,
  onClear, onToggleLock,
}) => {
  if (!value) {
    return (
      <Card>
        <CardLabel />
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>No current max</Text>
        <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
          The next result becomes current
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <CardLabel />
      <TopRow>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>{value}</Text>
        {onClear && (
          <LinkButton type='button' disabled={isClearing} onClick={onClear}>Clear</LinkButton>
        )}
      </TopRow>
      <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
        {[formatDate(dateSet), sourceLabel].filter(Boolean).join(' · ')}
      </Text>
      {onToggleLock && (
        <ToggleRow>
          {/* The shared Toggle only takes a starting value so remount it when the lock changes */}
          <Toggle
            key={String(isLocked)}
            defaultChecked={isLocked}
            disabled={isUpdatingLock}
            width='auto'
            onChange={onToggleLock}
          />
          <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Lock to prevent athlete from updating</Text>
        </ToggleRow>
      )}
      {isLocked && (
        <LockNote>
          {onToggleLock
            ? `${athleteFirst}'s new results still save to history, but won't change the current max or complete a goal.`
            : 'Your coach has locked this max. New results still save to history, but won\'t change it.'}
        </LockNote>
      )}
    </Card>
  );
};

CurrentMaxCard.propTypes = {
  value: PropTypes.string,
  dateSet: PropTypes.string,
  sourceLabel: PropTypes.string,
  athleteFirst: PropTypes.string,
  isLocked: PropTypes.bool,
  isUpdatingLock: PropTypes.bool,
  isClearing: PropTypes.bool,
  onClear: PropTypes.func,
  onToggleLock: PropTypes.func,
};

CurrentMaxCard.defaultProps = {
  value: null,
  dateSet: null,
  sourceLabel: null,
  athleteFirst: 'This athlete',
  isLocked: false,
  isUpdatingLock: false,
  isClearing: false,
  onClear: null,
  onToggleLock: null,
};

export default CurrentMaxCard;
